import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { NavController } from '@ionic/angular';
import { Router } from '@angular/router';
import { SsoService } from '../common/sharedServices/sso/sso.service';
import { AuthenticationService } from '../common/sharedServices/authentication/authentication.service';
import { lastValueFrom, retry } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit {
  appVersion = environment.APP_VERSION;
  showSpinner = true;
  loginInProgress = false;
  loginForm: UntypedFormGroup;
  showLoginError = false;
  loginErrorMessage: string;

  passwordType = 'password';
  passwordIcon = 'eye-off';

  constructor(
    public navCtrl: NavController,
    private formBuilder: UntypedFormBuilder,
    private SSO: SsoService,
    private authenticationService: AuthenticationService,
    private router: Router,
    protected translateService: TranslateService,
  ) {
    this.loginForm = this.formBuilder.group({
      alias: ['', Validators.required],
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  ngOnInit() {
    this.caricaPreferenzeDiLogin();
  }

  /**
   * Carica dal DB i dati di username e Ip del server
   * inseriti nel precedente login, se disponibili
   */
  private async caricaPreferenzeDiLogin() {
    const oldLoginData = await this.SSO.getOldLoginData();
    if (oldLoginData) { // se e' stato fatto un login in precedenza
      this.loginForm.controls['alias'].setValue(oldLoginData.alias);
      this.loginForm.controls['username'].setValue(oldLoginData.username);
      this.loginForm.controls['password'].setValue(oldLoginData.password);
      this.showSpinner = false;

      if (oldLoginData.password) {
        // L'utente e' loggato (non ha premuto il logout nella sessione precedente)
        this.performLogin(oldLoginData.username, oldLoginData.password, oldLoginData.alias, true, oldLoginData.serverName);
      }
    }
  }

  async loginFormSubmit() {
    const alias = this.loginForm.controls['alias'].value;
    const username = this.loginForm.controls['username'].value;
    const password = this.loginForm.controls['password'].value;

    const serverLicenzeResponse = await lastValueFrom(
      this.authenticationService.getUrls(username, password, alias).pipe(retry(3))
    );

    const serverLicenze = serverLicenzeResponse.imsrvlicappsdt;
    
    if (serverLicenzeResponse.imsrvlicappsdt) {
      this.performLogin(username, password, alias, false, serverLicenze.url_wsrest);
    } else {
      const errorMessage = `Alias ${alias} ${this.translateService.instant('Login.aliasError')}`;
      this.showLoginError = true;
      this.loginErrorMessage = errorMessage;
      throw Error(errorMessage);
    }
  }

  /**
   * Tentativo di fare login, prima online contattando il backend e, nel caso di fallimento e 
   * se la sessione precedente era valida 
   * @param username credenziale immessa da memorizzare se il login va
   * a buon fine
   * @param password credenziale immessada memorizzare se il login va
   * a buon fine
   * @param serverIP indirizzo ip del backend immesso da memorizzare se il login va
   * a buon fine
   * @param serverAlias etichetta alternativa immessa dall'utente da memorizzare se il login va
   * a buon fine
   * @param sessionePrecedenteValida true se l'utente era precedentemente loggato e quindi, se anche non
   * si riesce a fare il login contattando il backend, la navigazione può procedere
   */
  private async performLogin(
    username: string,
    password: string,
    alias: string,
    sessionePrecedenteValida: boolean,
    serverAddress?: string,
  ): Promise<any> {
    // ONLINE

    if (await this.performLoginToServer(alias, username, password, serverAddress)) {
      // Memorizzo le credenziali immesse
      this.SSO.setSSOLoginData(username, password, alias, serverAddress);
      this.avanzaNavigazione();
    } else {
      // Login online fallito; se ero gia' loggato posso comunque  procedere
      if (sessionePrecedenteValida) {
        console.warn('Login concesso anche se offline');
        this.avanzaNavigazione();

      } else {

        this.showLoginError = true;
        //this.loginErrorMessage = 'Errore login';
      }
    }

  }

  /**
   * Cerca di estrarre l'indirizzo ip di uno dei clienti noti attraverso
   * l'associazione univoca etichetta (resa maiuscola) e indirizzo memorizzato
   * in app
   * @param alias 
   * @returns l'indirizzo ip associato oppure null
   */
  // private getServerAddressFromAlias(alias: string): string | null {
  //   const CLIENTI_NOTI_APP = {
  //     AGO: 'http://172.20.1.12:8081/imse',
  //     AGOEXT: 'https://grsvil14.agomir.com:8444/imse',
  //     DEMO: 'https://demo01.agomir.com:8443/imse',
  //     DEMOALT: 'https://demo01.agomir.com:8443/imseALT',
  //     LOCAL: 'http://localhost:8080/imse',
  //     // TODO: Aggiungere gli IP pubblici dei clienti
  //     NOV: 'http://integra.novastilmec.it:8081/imse',
  //     NOVS: 'https://integra.novastilmec.it:8444/imse',
  //     TER: 'https://app.tecnorobot.com:8444/imse',
  //     TCL: 'https://tecnolarioerp.agomir.com:8444/imse'
  //   };
  //   alias = alias.toUpperCase();
  //   const serverCliente = CLIENTI_NOTI_APP[alias];
  //   return serverCliente;
  // }

  private async performLoginToServer(alias: string, username: string, password: string, serverIP: string): Promise<boolean> {
    this.showSpinner = true;
    this.loginInProgress = true;
    try {
      await this.authenticationService.login(alias, serverIP, username, password);
      this.showLoginError = false;
      return true;
    } catch (error) {

      this.showLoginError = true;
      this.loginErrorMessage = error.message;
      return false;
    } finally {
      this.showSpinner = false;
      this.loginInProgress = false;
    }
  }

  private async avanzaNavigazione() {
    this.router.navigateByUrl('/interventi');
  }

  hideShowPassword() {
    this.passwordType = this.passwordType === 'text' ? 'password' : 'text';
    this.passwordIcon = this.passwordIcon === 'eye-off' ? 'eye' : 'eye-off';
  }
}
